import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Divider,
    Card,
    CardContent,
    Grid
} from '@mui/material';
import { 
    CheckCircle, 
    RadioButtonUnchecked, 
    Info, 
    InsertDriveFile,
    Person,
    CalendarToday
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { buildApiUrl } from '../config/api';

function AdminSentenceFiles() {
    const { token } = useAuth();
    const [files, setFiles] = useState([]);
    const [activeFile, setActiveFile] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [statsDialog, setStatsDialog] = useState(null);
    const [editDialog, setEditDialog] = useState(null);
    const [editDescription, setEditDescription] = useState('');

    const fetchFiles = useCallback(async () => {
        setIsLoading(true);
        setError('');
        try {
            const response = await fetch(buildApiUrl('admin/sentence-files'), {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || `Erro ${response.status}`);
            }
            const data = await response.json();
            setFiles(data.files || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, [token]);

    const fetchActiveFile = useCallback(async () => {
        try {
            const response = await fetch(buildApiUrl('admin/sentence-files/active'), {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });
            if (response.ok) {
                const data = await response.json();
                setActiveFile(data);
            }
        } catch (err) {
            // Active file not found is ok
        }
    }, [token]);

    useEffect(() => {
        fetchFiles();
        fetchActiveFile();
    }, [fetchFiles, fetchActiveFile]);

    const handleActivate = async (fileId) => {
        setError('');
        setSuccess('');
        try {
            const response = await fetch(buildApiUrl(`admin/sentence-files/${fileId}/activate`), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || `Erro ${response.status}`);
            }
            setSuccess('Arquivo ativado com sucesso!');
            fetchFiles();
            fetchActiveFile();
        } catch (err) {
            setError(err.message);
        }
    };

    // Exclusão removida para preservar histórico de dados

    const handleShowStats = async (fileId) => {
        try {
            const response = await fetch(buildApiUrl(`admin/sentence-files/${fileId}/stats`), {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || `Erro ${response.status}`);
            }
            const stats = await response.json();
            setStatsDialog(stats);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleEditDescription = (file) => {
        setEditDialog(file);
        setEditDescription(file.description || '');
    };

    const handleSaveDescription = async () => {
        if (!editDialog) return;
        setError('');
        setSuccess('');
        try {
            const response = await fetch(buildApiUrl(`admin/sentence-files/${editDialog.id}`), {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    description: editDescription
                }),
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || `Erro ${response.status}`);
            }
            setSuccess('Descrição atualizada com sucesso!');
            setEditDialog(null);
            fetchFiles();
        } catch (err) {
            setError(err.message);
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString('pt-BR');
    };

    return (
        <Box sx={{ mt: 4 }}>
            <Typography variant="h5" gutterBottom>
                Gerenciar Arquivos de Sentenças
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                Visualize e gerencie os arquivos CSV importados com suas versões e metadados.
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            {/* Active File Card */}
            {activeFile && (
                <Card sx={{ mb: 3, backgroundColor: '#e8f5e8' }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                            <CheckCircle color="success" sx={{ mr: 1 }} />
                            Arquivo Ativo Atual
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="textSecondary">Nome do Arquivo:</Typography>
                                <Typography variant="body1">{activeFile.filename}</Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="textSecondary">Data de Upload:</Typography>
                                <Typography variant="body1">{formatDate(activeFile.upload_date)}</Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="textSecondary">Sentenças:</Typography>
                                <Typography variant="body1">{activeFile.sentence_count}</Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="textSecondary">Tamanho:</Typography>
                                <Typography variant="body1">{formatFileSize(activeFile.file_size)}</Typography>
                            </Grid>
                            {activeFile.description && (
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="textSecondary">Descrição:</Typography>
                                    <Typography variant="body1">{activeFile.description}</Typography>
                                </Grid>
                            )}
                        </Grid>
                    </CardContent>
                </Card>
            )}

            {/* Files Table */}
            <Paper elevation={2}>
                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Nome do Arquivo</TableCell>
                                    <TableCell>Upload</TableCell>
                                    <TableCell>Enviado por</TableCell>
                                    <TableCell>Sentenças</TableCell>
                                    <TableCell>Tamanho</TableCell>
                                    <TableCell>Ações</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {files.map((file) => (
                                    <TableRow key={file.id}>
                                        <TableCell>
                                            {file.is_active ? (
                                                <Chip 
                                                    icon={<CheckCircle />} 
                                                    label="Ativo" 
                                                    color="success" 
                                                    size="small" 
                                                />
                                            ) : (
                                                <Chip 
                                                    icon={<RadioButtonUnchecked />} 
                                                    label="Inativo" 
                                                    color="default" 
                                                    size="small" 
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <InsertDriveFile sx={{ mr: 1, fontSize: 18 }} />
                                                <Box>
                                                    <Typography variant="body2">{file.filename}</Typography>
                                                    {file.description && (
                                                        <Typography variant="caption" color="textSecondary">
                                                            {file.description}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <CalendarToday sx={{ mr: 1, fontSize: 16 }} />
                                                <Typography variant="body2">
                                                    {formatDate(file.upload_date)}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Person sx={{ mr: 1, fontSize: 16 }} />
                                                <Typography variant="body2">
                                                    {file.uploader_username || 'N/A'}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>{file.sentence_count}</TableCell>
                                        <TableCell>{formatFileSize(file.file_size)}</TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                {!file.is_active && (
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="primary"
                                                        onClick={() => handleActivate(file.id)}
                                                    >
                                                        Ativar
                                                    </Button>
                                                )}
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => handleShowStats(file.id)}
                                                    startIcon={<Info />}
                                                >
                                                    Stats
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => handleEditDescription(file)}
                                                >
                                                    Editar
                                                </Button>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            {/* Stats Dialog */}
            <Dialog open={!!statsDialog} onClose={() => setStatsDialog(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Estatísticas do Arquivo</DialogTitle>
                <DialogContent>
                    {statsDialog && (
                        <Box>
                            <Typography variant="h6" gutterBottom>{statsDialog.filename}</Typography>
                            <Divider sx={{ my: 2 }} />
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="textSecondary">Sentenças no arquivo:</Typography>
                                    <Typography variant="body1">{statsDialog.sentences_count}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="textSecondary">QR Codes gerados:</Typography>
                                    <Typography variant="body1">{statsDialog.qrcodes_count}</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="textSecondary">Status:</Typography>
                                    <Chip 
                                        label={statsDialog.is_active ? 'Ativo' : 'Inativo'} 
                                        color={statsDialog.is_active ? 'success' : 'default'} 
                                        size="small" 
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="textSecondary">Data de Upload:</Typography>
                                    <Typography variant="body1">{formatDate(statsDialog.upload_date)}</Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setStatsDialog(null)}>Fechar</Button>
                </DialogActions>
            </Dialog>

            {/* Edit Description Dialog */}
            <Dialog open={!!editDialog} onClose={() => setEditDialog(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Editar Descrição</DialogTitle>
                <DialogContent>
                    {editDialog && (
                        <Box sx={{ pt: 1 }}>
                            <Typography variant="body1" gutterBottom>
                                Arquivo: {editDialog.filename}
                            </Typography>
                            <TextField
                                fullWidth
                                label="Descrição"
                                multiline
                                rows={3}
                                value={editDescription}
                                onChange={(e) => setEditDescription(e.target.value)}
                                placeholder="Digite uma descrição para este arquivo..."
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialog(null)}>Cancelar</Button>
                    <Button onClick={handleSaveDescription} variant="contained">Salvar</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default AdminSentenceFiles;